/**
 * `useTableInputsEdit()` — TanStack Mutation wrapper for `POST /table-edit`.
 *
 * Mirrors `useModelEdit` (apps/web/src/hooks/use-model-edit.ts:67-285) exactly:
 * the scalar dual-cache optimistic pattern applied at table-input granularity.
 *
 * ## Wire vs optimistic split (design §14 Session 3 "Hook input refinement"):
 *
 * The mutation vars carry TWO representations of the same write:
 *   - `writes: Record<namedRange, number>` — named-range-keyed map used for the
 *     OPTIMISTIC PATCH in `onMutate` + `onSuccess` cache merge. These are the
 *     same keys that `stateDelta.written` returns (composite named ranges like
 *     `Studio_Rent_Month`). NOT sent over the wire.
 *   - `displayRows: { label, values }[]` — row-label + colKey pairs sent as the
 *     wire body `{ table, rows: displayRows, editId }`. The server resolves
 *     named ranges server-side from the schema.
 *
 * This keeps the hook's optimistic merge byte-aligned with `useModelEdit`
 * (both key by named range) while the server receives the schema-portable shape.
 *
 * ## Cross-domain contract (design §7):
 * The response `stateDelta.written` carries COMPOSITE-CELL NAMED-RANGE keys
 * (e.g. `Studio_Rent_Month`), NOT display paths (`UnitMix.Studio.rent`).
 * These are valid `inputValues` / `inputProvenance` keys and merge cleanly.
 * The `builder.tsx` post-stream composite-key skip is UNCHANGED — it guards
 * against agent-path SSE keys; this hook's onSuccess is the only path that
 * surfaces named-range keys for table cells.
 *
 * `retry: 0` — matches `useModelEdit`; `editId` is generated once per mutate
 * call (crypto.randomUUID()) for idempotency; retry > 0 would generate a new
 * editId per attempt and bypass server-side dedupe.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type ModelEditResponse } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

export interface TableInputsEditMutationVars {
  conversationId: string;
  /** Table name as defined in `TableInputDefinition.name` (wire body field). */
  table: string;
  /**
   * Named-range-keyed write map (e.g. `{ Studio_Rent_Month: 2200 }`).
   * Drives the OPTIMISTIC PATCH only — NOT sent on the wire.
   * Built by `buildWritesFromGrid` from the hydrated grid output.
   */
  writes: Record<string, number>;
  /**
   * Wire-format rows: `{ label, values: Record<colKey, number> }[]`.
   * Sent as `body.rows` to the endpoint. Server resolves named ranges.
   */
  displayRows: Array<{ label: string; values: Record<string, number> }>;
}

/**
 * Snapshot of both caches captured in `onMutate`, used for rollback on error.
 * Stored as plain `unknown` to avoid coupling this hook to internal cache shapes
 * (mirrors `useModelEdit`'s `MutationContext`).
 */
interface MutationContext {
  prevDetail: unknown;
  prevViews: unknown;
}

export function useTableInputsEdit() {
  const queryClient = useQueryClient();
  return useMutation<
    ModelEditResponse,
    ApiError,
    TableInputsEditMutationVars,
    MutationContext
  >({
    mutationFn: ({ conversationId, table, displayRows }) =>
      api.postTableEdit(conversationId, {
        table,
        rows: displayRows,
        editId: crypto.randomUUID(),
      }),
    retry: 0,
    onMutate: async (vars): Promise<MutationContext> => {
      // Cancel any in-flight refetches so they don't overwrite our patch.
      await queryClient.cancelQueries({
        queryKey: queryKeys.conversations.detail(vars.conversationId),
      });
      await queryClient.cancelQueries({
        queryKey: queryKeys.conversations.views(vars.conversationId),
      });

      // Snapshot both caches for rollback.
      const prevDetail = queryClient.getQueryData(
        queryKeys.conversations.detail(vars.conversationId)
      );
      const prevViews = queryClient.getQueryData(
        queryKeys.conversations.views(vars.conversationId)
      );

      // Build optimistic ProvenanceRecords keyed by named range.
      // Mirror `useModelEdit`'s `optimisticRecord` shape (lines 114-120).
      const now = new Date().toISOString();
      const optimisticRecords: Record<string, unknown> = {};
      for (const [namedRange, value] of Object.entries(vars.writes)) {
        optimisticRecords[namedRange] = {
          inputName: namedRange,
          value,
          source: 'user' as const,
          confidence: 1,
          timestamp: now,
        };
      }

      // Patch the LEGACY detail cache (used by `useConversation` shim →
      // VariablesPanel). Mirror `useModelEdit` lines 125-156.
      queryClient.setQueryData(
        queryKeys.conversations.detail(vars.conversationId),
        (prev: unknown) => {
          if (!prev || typeof prev !== 'object') return prev;
          const detail = prev as {
            data?: {
              agentState?: {
                inputValues?: Record<string, unknown>;
                inputProvenance?: Record<string, unknown>;
              } | null;
            };
          };
          if (!detail.data?.agentState) return prev;
          const inputValuesOptimistic: Record<string, unknown> = {};
          for (const [namedRange, value] of Object.entries(vars.writes)) {
            inputValuesOptimistic[namedRange] = value;
          }
          return {
            ...detail,
            data: {
              ...detail.data,
              agentState: {
                ...detail.data.agentState,
                inputValues: {
                  ...(detail.data.agentState.inputValues ?? {}),
                  ...inputValuesOptimistic,
                },
                inputProvenance: {
                  ...(detail.data.agentState.inputProvenance ?? {}),
                  ...optimisticRecords,
                },
              },
            },
          };
        }
      );

      // Patch the SSOT views cache (used by Model + Signal + shim).
      // Mirror `useModelEdit` lines 159-174.
      queryClient.setQueryData(
        queryKeys.conversations.views(vars.conversationId),
        (prev: unknown) => {
          if (!prev || typeof prev !== 'object') return prev;
          const views = prev as { inputs?: Record<string, unknown> };
          if (!views.inputs) return prev;
          return {
            ...views,
            inputs: {
              ...views.inputs,
              ...optimisticRecords,
            },
          };
        }
      );

      return { prevDetail, prevViews };
    },
    onError: (_err, vars, context) => {
      // Roll back both caches to their pre-mutation snapshots.
      // Mirror `useModelEdit` lines 178-194.
      if (context?.prevDetail !== undefined) {
        queryClient.setQueryData(
          queryKeys.conversations.detail(vars.conversationId),
          context.prevDetail
        );
      }
      if (context?.prevViews !== undefined) {
        queryClient.setQueryData(
          queryKeys.conversations.views(vars.conversationId),
          context.prevViews
        );
      }
    },
    onSuccess: (data, vars) => {
      // Merge server-authoritative state. The onMutate patch already wrote
      // optimistic values; this overwrites with the canonical post-edit state.
      // Mirror `useModelEdit` lines 195-271.

      // Detail cache: inputProvenance ← full records; inputValues ← stateDelta.written
      queryClient.setQueryData(
        queryKeys.conversations.detail(vars.conversationId),
        (prev: unknown) => {
          if (!prev || typeof prev !== 'object') return prev;
          const detail = prev as {
            data?: {
              agentState?: {
                inputValues?: Record<string, unknown>;
                inputProvenance?: Record<string, unknown>;
              } | null;
            };
          };
          if (!detail.data?.agentState) return prev;
          return {
            ...detail,
            data: {
              ...detail.data,
              agentState: {
                ...detail.data.agentState,
                inputValues: {
                  ...(detail.data.agentState.inputValues ?? {}),
                  ...data.stateDelta.written,
                },
                inputProvenance: {
                  ...(detail.data.agentState.inputProvenance ?? {}),
                  ...data.inputProvenance,
                },
              },
            },
          };
        }
      );

      // Views cache: inputs[k] ← full ProvenanceRecord from inputProvenance.
      queryClient.setQueryData(
        queryKeys.conversations.views(vars.conversationId),
        (prev: unknown) => {
          if (!prev || typeof prev !== 'object') return prev;
          const views = prev as { inputs?: Record<string, unknown> };
          if (!views.inputs) return prev;
          return {
            ...views,
            inputs: {
              ...views.inputs,
              ...data.inputProvenance,
            },
          };
        }
      );
    },
    onSettled: (_data, _err, vars) => {
      // Safety-net invalidation. Idempotent with the onSuccess patch.
      // Mirror `useModelEdit` lines 273-283.
      queryClient.invalidateQueries({
        queryKey: queryKeys.conversations.detail(vars.conversationId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.conversations.views(vars.conversationId),
      });
    },
  });
}
